<template>
  <div class="sale-page">
    <block-buttons
      title="Продажи"
      description="История покупок, оплата и возвраты"
      :actions="toolbarActions"
    >
      <template #icon>
        <ShoppingBag :size="22" />
      </template>
    </block-buttons>

    <form
      class="sale-page__filters"
      @submit.prevent="loadDataWrapper"
    >
      <label>
        Период
        <select
          v-model="period"
          :disabled="loading"
          @change="selectPeriod"
        >
          <option value="1">За месяц</option>
          <option value="3">За 3 месяца</option>
          <option value="6">За 6 месяцев</option>
          <option value="custom">Произвольный период</option>
        </select>
      </label>

      <label>
        С
        <input
          v-model="dateFrom"
          type="text"
          placeholder="ДД.ММ.ГГГГ"
          maxlength="10"
          required
        />
      </label>

      <label>
        По
        <input
          v-model="dateTo"
          type="text"
          placeholder="ДД.ММ.ГГГГ"
          maxlength="10"
          required
        />
      </label>

      <button
        type="submit"
        :disabled="loading"
      >
        Показать
      </button>
    </form>

    <data-table
      :rows="renderData"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      :search-keys="['productListName', 'productCodes', 'typePay', 'price', 'createdAtFormatted']"
      title="История продаж"
      description="Продажи за выбранный период"
      search-placeholder="Товар, код, способ оплаты или дата"
      empty-text="За выбранный период продаж нет"
      @retry="loadDataWrapper"
    >
      <template #header>
        <span class="sale-page__count">Всего продаж: {{ sales.length }}</span>
      </template>

      <template #cell-productListName="{ row }">
        <strong>{{ row.productListName }}</strong>
        <small
          v-if="row.isReturned"
          class="sale-page__returned"
        >
          Возврат
        </small>
      </template>

      <template #cell-price="{ row }">
        <strong>{{ priceToFormat(row.price) }}</strong>
      </template>

      <template #cell-isReturned="{ row }">
        {{ row.isReturned ? 'Возврат' : 'Продажа' }}
      </template>

      <template #cell-actions="{ row }">
        <select
          :disabled="loading"
          :aria-label="`Действия с продажей ${row.id}`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions(row)"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ShoppingBag, Plus, RefreshCw } from 'lucide-vue'
import moment from 'moment'
import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import permissions from '@/mixins/permissions'
import { priceToFormat } from '@/helper'

export default {
  name: 'SaleV2',
  components: {
    ShoppingBag,
    BlockButtons,
    DataTable
  },
  mixins: [permissions],
  data () {
    return {
      loading: false,
      error: '',
      period: '1',
      dateFrom: moment().startOf('month').format('DD.MM.YYYY'),
      dateTo: moment().format('DD.MM.YYYY')
    }
  },
  computed: {
    ...mapGetters('sale', { sales: 'getData' }),
    ...mapGetters('product', ['getDataObject']),
    ...mapGetters('settings', ['getTableColumns', 'getDefaultValues']),
    ...mapGetters('workingShift', ['getOpenedWorkingShift']),
    ...mapGetters('config', ['getIsOffline']),
    renderData () {
      const paymentTypes = this.getDefaultValues?.['type-pay'] || []

      return this.sales.map(sale => ({
        ...sale,
        productListName: Array.isArray(sale.productListName) ? sale.productListName.join(', ') : sale.productListName,
        productCodes: (sale.productList || []).map(id => this.getDataObject[id]?.code).filter(Boolean).join(' '),
        typePayText: paymentTypes.find(({ code }) => code === sale.typePay)?.name,
        renderPrice: priceToFormat(sale.price),
        renderPurchasePrice: priceToFormat(sale.purchasePrice)
      }))
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['sale'] || [])
      ]
    },
    toolbarActions () {
      const actions = [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: this.loadDataWrapper
        }
      ]

      if (this.isAllowedOperation('create', 'sale')) {
        actions.push({
          id: 'create',
          label: 'Новая продажа',
          icon: Plus,
          primary: true,
          disabled: this.loading || !this.getOpenedWorkingShift,
          handler: () => this.openModal({ name: 'create-sale' })
        })
      }

      return actions
    }
  },
  mounted () {
    this.loadDataWrapper()
  },
  methods: {
    ...mapActions('sale', ['loadData', 'remove', 'return']),
    ...mapActions('workingShift', { loadWorkingShifts: 'loadData' }),
    ...mapActions('modal', ['openModal', 'closeModal']),
    priceToFormat,
    async loadDataWrapper () {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      await this.loadData([this.dateFrom, this.dateTo])

      this.loading = false
      this.$bus.emit('stop-loader')
    },
    selectPeriod () {
      if (this.period === 'custom') {
        return
      }

      this.dateFrom = moment().subtract(Number(this.period), 'month').format('DD.MM.YYYY')
      this.dateTo = moment().format('DD.MM.YYYY')

      this.loadDataWrapper()
    },
    rowActions (row) {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'change',
          label: 'Редактировать',
          visible: this.isAllowedOperation('change', 'sale')
        },
        {
          id: 'return',
          label: 'Вернуть продажу',
          visible: !row.isReturned
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'sale')
        }
      ].filter(({ visible }) => visible)
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction(action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'sale',
              moduleAction: 'sale',
              type: 'info',
              title: `Информация - ${row.createdAtFormatted} ${row.price}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'change':
          this.openModal({
            name: 'change-base',
            propsData: {
              name: 'sale',
              moduleAction: 'sale',
              type: 'change',
              title: `Редактирование - ${row.createdAtFormatted} ${row.price}`,
              text: 'Измените необходимые данные',
              textCancelBtn: 'Закрыть',
              textConfirmBtn: 'Сохранить',
              data: row
            }
          })

          break
        case 'return':
          this.returnSale(row)

          break
        case 'remove':
          this.removeSale(row)
      }
    },
    async removeSale ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении продажи!',
          clean: false,
          duration: 3000
        })

        return
      }

      await this.loadDataWrapper()

      this.$notify({
        type: 'success',
        title: 'Продажа успешно удалена!',
        text: '',
        clean: false,
        duration: 3000
      })
    },
    async returnSale ({ id }) {
      const { error } = await this.return(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при возврате продажи!',
          text: 'Произошла ошибка при возврате продажи!',
          clean: false,
          duration: 3000
        })

        return
      }

      await this.loadDataWrapper()

      this.$notify({
        type: 'success',
        title: 'Продажа успешно возвращена!',
        text: '',
        clean: false,
        duration: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
